import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, Trash2, Calendar as CalIcon, ChefHat, Camera, Pin, PinOff, ChevronDown, ChevronUp, Pencil } from "lucide-react";
import { MemberAvatar } from "@/components/Atoms";
import { BottomSheet } from "@/components/BottomSheet";
import { ItemActionSheet } from "@/components/ItemActionSheet";
import { ListActionSheet } from "@/components/ListActionSheet";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";
import { useEntitlement } from "@/context/EntitlementsContext";
import { api, getCached } from "@/lib/api";
import { fmtRelativeDate } from "@/lib/format";
import { useLongPress } from "@/lib/useLongPress";
import { EmptyHint, ScopeChips, useScope } from "./Shared";

export function TodosView({ refreshKey, onCreated }) {
  const { family, members: familyMembers, user } = useAuth();
  const { scope, setScope, me, scopePersonId } = useScope(family, familyMembers, user?.id);
  // Cache primer — instant render when tab returns to /plan?tab=todos
  const cLists = family ? getCached(`/families/${family.id}/todo-lists`) : null;
  const [lists, setLists] = useState(cLists || []);
  // Members are now sourced from AuthContext (single shared roster) — no more
  // per-screen /members fetch. Falls back to the cached snapshot if context
  // hasn't hydrated yet (e.g. very first cold start with no IDB cache).
  const [members, setMembers] = useState(familyMembers || []);
  const [active, setActive] = useState(cLists?.[0] || null);
  const [items, setItems] = useState(() =>
    family && cLists?.[0]
      ? getCached(`/families/${family.id}/todo-lists/${cLists[0].id}/items`) || []
      : []
  );
  const [text, setText] = useState("");
  // Default the inline composer to the current user — when a creator
  // doesn't think about it, the to-do is theirs. Visible in the chip.
  const [assigneeId, setAssigneeId] = useState(me?.id || null);
  // Keep the default in sync once `me` is known (familyMembers can hydrate
  // a frame later than the first paint on a cold start).
  useEffect(() => { if (me && !assigneeId) setAssigneeId(me.id); /* eslint-disable-next-line */ }, [me]);
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);
  // Entitlement-driven paywall: when `todo_lists.cap` is a finite number and
  // `lists.length >= cap`, we surface the upgrade. The actual open() call
  // happens via setPaywall() below — kept as a local flag for the few
  // synchronous code paths that check it; the actual SHEET is the global one.
  const todoListsCap = useEntitlement("todo_lists.cap");
  const { openPaywall } = usePaywall();
  const setPaywall = (on) => {
    if (on) openPaywall({
      featureKey: "todo_lists.cap",
      requiredTier: todoListsCap.requiredTier || "spark",
      message: "Free families get 2 to-do lists. Upgrade for unlimited lists, unlimited Quick-Add, and the full calendar.",
    });
  };
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [listActionTarget, setListActionTarget] = useState(null);
  const [subEditTarget, setSubEditTarget] = useState(null); // {todoId, sub: {id, title}}

  const loadAll = async () => {
    if (!family) return;
    // Drop the per-screen /members fetch — AuthContext owns it now.
    const ls = await api.get(`/families/${family.id}/todo-lists`);
    setLists(ls.data);
    setMembers(familyMembers || []);
    if (ls.data.length && !active) setActive(ls.data[0]);
  };
  const loadItems = async () => {
    if (!family || !active) { setItems([]); return; }
    const params = scopePersonId ? { assignee: scopePersonId } : {};
    const { data } = await api.get(`/families/${family.id}/todo-lists/${active.id}/items`, { params });
    setItems(data);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [family, refreshKey]);
  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [active, refreshKey, scope]);
  useEffect(() => { setMembers(familyMembers || []); }, [familyMembers]);

  const createList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    try {
      const { data } = await api.post(`/families/${family.id}/todo-lists`, { name: newListName.trim() });
      setNewListName(""); setCreatingList(false);
      await loadAll();
      setActive(data);
    } catch (err) {
      if (err.response?.status === 402) setPaywall(true);
    }
  };

  const addTodo = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    await api.post(`/families/${family.id}/todo-lists/${active.id}/items`, {
      title: text.trim(),
      assignee_person_id: assigneeId || undefined,
    });
    setText(""); setAssigneeId(null);
    loadItems();
  };

  const toggle = async (t) => {
    setItems((arr) => arr.map((x) => x.id === t.id ? { ...x, done: !x.done } : x));
    await api.patch(`/families/${family.id}/todo-lists/${active.id}/items/${t.id}`, { done: !t.done });
  };

  const removeTodo = async (t) => {
    setItems((arr) => arr.filter((x) => x.id !== t.id));
    await api.delete(`/families/${family.id}/todo-lists/${active.id}/items/${t.id}`);
  };

  const onPromoteRequest = (t) => setPromoteTarget(t);
  const onPromoteConfirm = async ({ start_at, all_day }) => {
    if (!promoteTarget) return;
    await api.post(
      `/families/${family.id}/todo-lists/${active.id}/items/${promoteTarget.id}/promote`,
      { start_at, all_day },
    );
    setPromoteTarget(null);
    loadItems();
    onCreated?.();
  };

  const toggleExpanded = (id) => {
    setExpanded((s) => {
      const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });
  };

  const addChecklistItem = async (todoId, title) => {
    await api.post(`/families/${family.id}/todo-lists/${active.id}/items/${todoId}/checklist`, { title });
    loadItems();
  };
  const toggleChecklistItem = async (todoId, subId, done) => {
    // Optimistic update — flip the sub-item locally BEFORE the network call.
    // Without this the user sees zero feedback for the entire Railway round-
    // trip (8-15s on cold dyno). Match the parent `toggle()` instant pattern.
    setItems((arr) => arr.map((t) => {
      if (t.id !== todoId) return t;
      const checklist = (t.checklist || []).map((c) =>
        c.id === subId ? { ...c, done } : c
      );
      return { ...t, checklist };
    }));
    try {
      await api.patch(
        `/families/${family.id}/todo-lists/${active.id}/items/${todoId}/checklist/${subId}`,
        { done },
      );
    } catch (_e) {
      // Revert on failure — UI returns to truth.
      setItems((arr) => arr.map((t) => {
        if (t.id !== todoId) return t;
        const checklist = (t.checklist || []).map((c) =>
          c.id === subId ? { ...c, done: !done } : c
        );
        return { ...t, checklist };
      }));
    }
  };

  const sections = useMemo(() => groupTodosBySection(items), [items]);

  useEffect(() => {
    if (!active || !text.trim() || text.trim().length < 2) {
      setSuggestions([]); return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/families/${family.id}/todo-lists/${active.id}/suggestions`,
          { params: { q: text.trim() } },
        );
        setSuggestions((data?.suggestions || []).filter((s) => s.title.toLowerCase() !== text.trim().toLowerCase()).slice(0, 5));
      } catch { setSuggestions([]); }
    }, 200);
    return () => clearTimeout(handle);
  }, [text, active, family]);

  const pickSuggestion = async (title) => {
    setText(""); setSuggestions([]); setShowSuggest(false);
    await api.post(`/families/${family.id}/todo-lists/${active.id}/items`, {
      title,
      assignee_person_id: assigneeId || undefined,
    });
    loadItems();
  };

  return (
    <div className="space-y-4" data-testid="todos-view">
      <ScopeChips scope={scope} onScopeChange={setScope} members={familyMembers || []} me={me} />
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 scrollbar-hide">
        {lists.map((l) => (
          <ListChip
            key={l.id}
            list={l}
            active={active?.id === l.id}
            onTap={() => setActive(l)}
            onLongPress={() => setListActionTarget(l)}
          />
        ))}
        <button
          data-testid="new-list-btn"
          onClick={() => setCreatingList(true)}
          className="shrink-0 h-9 px-4 rounded-full tap text-sm font-medium border border-dashed border-border text-muted-foreground"
        >
          + List
        </button>
        {active && (
          <button
            data-testid="recipe-btn"
            onClick={() => setRecipeOpen(true)}
            className="shrink-0 h-9 px-3 rounded-full tap text-sm font-medium border border-primary/30 bg-primary/[0.06] text-primary inline-flex items-center gap-1.5"
            title="Paste a recipe — Kinly fans out the ingredients to this list"
          >
            <ChefHat className="w-3.5 h-3.5" />
            Recipe
          </button>
        )}
        {active && (
          <button
            data-testid="pin-day-btn"
            onClick={() => setPinTarget(active)}
            className={[
              "shrink-0 h-9 px-3 rounded-full tap text-sm font-medium border inline-flex items-center gap-1.5",
              active.pinned_for_day
                ? "border-amber-500/40 bg-amber-500/[0.08] text-amber-700 dark:text-amber-400"
                : "border-border text-muted-foreground",
            ].join(" ")}
            title={active.pinned_for_day
              ? `Pinned to ${active.pinned_for_day} — tap to change`
              : "Pin this list to a day"}
          >
            {active.pinned_for_day ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
            {active.pinned_for_day || "Pin to day"}
          </button>
        )}
      </div>

      {creatingList && (
        <div className="space-y-2">
          <form onSubmit={createList} className="kinly-card flex items-center gap-2">
            <input
              autoFocus
              data-testid="new-list-input"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="Groceries, Errands, Holiday packing…"
              className="flex-1 bg-transparent outline-none text-[15px] py-1"
            />
            <button type="submit" className="text-sm text-primary font-medium tap px-2">Create</button>
            <button type="button" onClick={() => setCreatingList(false)} className="text-sm text-muted-foreground tap px-2">Cancel</button>
          </form>
          <RecentStoreChips
            familyId={family?.id}
            onPick={(name) => setNewListName(name)}
          />
        </div>
      )}

      {!active ? (
        <EmptyHint
          eyebrow="To-dos"
          headline="Start your first list."
          body='Tap "+ List" above. Groceries, errands, holiday packing — whatever lives on a sticky note now.'
        />
      ) : (
        <>
          <form onSubmit={addTodo} className="bg-card border border-border rounded-2xl px-3 py-2 space-y-2 relative">
            <input
              data-testid="todo-input"
              value={text}
              onChange={(e) => { setText(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              placeholder={`Add to ${active.name}…`}
              className="w-full bg-transparent outline-none text-[15px]"
            />
            {showSuggest && suggestions.length > 0 && (
              <div
                data-testid="todo-suggestions"
                className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-2xl shadow-lg z-30 overflow-hidden"
              >
                {suggestions.map((s) => (
                  <button
                    key={s.title}
                    type="button"
                    data-testid={`todo-suggest-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                    onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s.title); }}
                    className="w-full text-left px-3 py-2 hover:bg-secondary/60 tap flex items-center justify-between text-[14px]"
                  >
                    <span>{s.title}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">×{s.count}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <AssigneePicker members={members} value={assigneeId} onChange={setAssigneeId} />
              <button
                type="submit"
                data-testid="todo-add"
                disabled={!text.trim()}
                className="text-primary text-sm font-medium tap px-2 disabled:text-muted-foreground"
              >
                Add
              </button>
            </div>
          </form>

          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-4">No items yet.</p>
          ) : (
            <div className="space-y-3">
              {sections.map(({ key, label, items: secItems }) => (
                secItems.length === 0 ? null : (
                  <section key={key} data-testid={`todo-section-${key}`}>
                    <h3 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium px-1 mb-1.5">
                      {label} · {secItems.length}
                    </h3>
                    <article className="kinly-card p-0 overflow-hidden divide-y divide-border/60">
                      {secItems.map((t) => (
                        <SwipeRow
                          key={t.id}
                          todo={t}
                          members={members}
                          expanded={expanded.has(t.id)}
                          onExpand={() => toggleExpanded(t.id)}
                          onToggle={() => toggle(t)}
                          onDelete={() => removeTodo(t)}
                          onPromote={() => onPromoteRequest(t)}
                          onLongPress={() => setActionTarget(t)}
                          onAddChecklist={(title) => addChecklistItem(t.id, title)}
                          onToggleChecklist={(subId, done) => toggleChecklistItem(t.id, subId, done)}
                          onLongPressSub={(sub) => setSubEditTarget({ todoId: t.id, sub })}
                        />
                      ))}
                    </article>
                  </section>
                )
              ))}
            </div>
          )}
        </>
      )}

      <ScheduleSheet
        open={!!promoteTarget}
        todo={promoteTarget}
        onClose={() => setPromoteTarget(null)}
        onConfirm={onPromoteConfirm}
      />

      <RecipeSheet
        open={recipeOpen}
        list={active}
        familyId={family?.id}
        onClose={() => setRecipeOpen(false)}
        onAdded={() => { setRecipeOpen(false); loadItems(); }}
      />

      <PinDaySheet
        open={!!pinTarget}
        list={pinTarget}
        familyId={family?.id}
        onClose={() => setPinTarget(null)}
        onPinned={(updated) => {
          setPinTarget(null);
          setLists((cur) => cur.map((l) => l.id === updated.id ? { ...l, ...updated } : l));
          if (active?.id === updated.id) setActive({ ...active, ...updated });
        }}
      />

      <ItemActionSheet
        open={!!actionTarget}
        kind="todo"
        item={actionTarget}
        familyId={family?.id}
        listId={active?.id}
        listName={active?.name}
        members={members}
        onChanged={loadItems}
        onClose={() => setActionTarget(null)}
      />

      <ListActionSheet
        open={!!listActionTarget}
        list={listActionTarget}
        lists={lists}
        familyId={family?.id}
        onChanged={async () => { await loadAll(); }}
        onClose={() => setListActionTarget(null)}
      />

      <SubItemEditSheet
        open={!!subEditTarget}
        sub={subEditTarget?.sub}
        busy={false}
        onClose={() => setSubEditTarget(null)}
        onSubmit={async (title) => {
          if (!subEditTarget) return;
          const { todoId, sub } = subEditTarget;
          // Optimistic — patch local list, then PATCH server.
          setItems((arr) => arr.map((t) => {
            if (t.id !== todoId) return t;
            const checklist = (t.checklist || []).map((c) =>
              c.id === sub.id ? { ...c, title } : c
            );
            return { ...t, checklist };
          }));
          setSubEditTarget(null);
          try {
            await api.patch(
              `/families/${family.id}/todo-lists/${active.id}/items/${todoId}/checklist/${sub.id}`,
              { title },
            );
          } catch (_e) {
            // Revert + reload from server on failure.
            loadItems();
          }
        }}
        onDelete={async () => {
          if (!subEditTarget) return;
          const { todoId, sub } = subEditTarget;
          setItems((arr) => arr.map((t) => {
            if (t.id !== todoId) return t;
            return { ...t, checklist: (t.checklist || []).filter((c) => c.id !== sub.id) };
          }));
          setSubEditTarget(null);
          try {
            await api.delete(
              `/families/${family.id}/todo-lists/${active.id}/items/${todoId}/checklist/${sub.id}`,
            );
          } catch (_e) { loadItems(); }
        }}
      />
    </div>
  );
}


function RecentStoreChips({ familyId, onPick }) {
  const [stores, setStores] = useState([]);

  useEffect(() => {
    if (!familyId) return;
    api.get(`/families/${familyId}/stores/recent?limit=6`)
      .then((r) => setStores(r.data || []))
      .catch(() => setStores([]));
  }, [familyId]);

  if (!stores.length) return null;
  return (
    <div className="px-3" data-testid="recent-store-chips">
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-1.5">
        Or use a favourite
      </p>
      <div className="flex gap-1.5 overflow-x-auto -mx-3 px-3 pb-1 scrollbar-hide">
        {stores.map((s) => (
          <button
            key={s.name}
            type="button"
            data-testid={`store-chip-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onPick?.(s.name)}
            className="shrink-0 h-7 px-3 rounded-full bg-secondary/70 hover:bg-secondary text-[12px] tap inline-flex items-center gap-1.5"
            title={`Used ${Math.round(s.weight)}×`}
          >
            <span className="w-1 h-1 rounded-full bg-foreground/40" />
            {s.name}
          </button>
        ))}
      </div>
    </div>
  );
}


function PinDaySheet({ open, list, familyId, onClose, onPinned }) {
  const todayIso = () => {
    const d = new Date();
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const [date, setDate] = useState(list?.pinned_for_day || todayIso());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setDate(list?.pinned_for_day || todayIso());
  }, [open, list?.pinned_for_day]);

  if (!open || !list) return null;

  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/families/${familyId}/todo-lists/${list.id}`,
        { pinned_for_day: value },
      );
      onPinned?.(data);
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={`Pin "${list.name}" to a day`}>
      <div className="space-y-4 pb-2" data-testid="pin-day-sheet">
        <p className="text-sm text-muted-foreground">
          A pinned list shows up on its day in your Day Planner so the whole list moves together — handy for shopping trips, errands, or weekly meal-prep.
        </p>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-1.5 block">
            Day
          </label>
          <input
            type="date"
            data-testid="pin-day-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-secondary rounded-xl px-3 py-3 outline-none text-[15px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          {list.pinned_for_day && (
            <button
              data-testid="pin-day-unpin"
              onClick={() => save(null)}
              disabled={busy}
              className="h-12 rounded-2xl border border-border text-foreground text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
            >
              <PinOff className="w-4 h-4" /> Unpin
            </button>
          )}
          <button
            data-testid="pin-day-save"
            onClick={() => save(date)}
            disabled={busy || !date}
            className={[
              list.pinned_for_day ? "" : "col-span-2",
              "h-12 rounded-2xl bg-foreground text-background text-sm font-medium tap inline-flex items-center justify-center gap-1.5 disabled:opacity-50",
            ].join(" ")}
          >
            <Pin className="w-4 h-4" /> Pin to this day
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

function AssigneePicker({ members, value, onChange }) {
  const sel = members.find((m) => m.id === value);
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">For</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          data-testid="assignee-anyone"
          onClick={() => onChange(null)}
          className={[
            "h-6 px-2 rounded-full border text-[11px] tap font-medium",
            !value ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground",
          ].join(" ")}
        >
          Anyone
        </button>
        {members.slice(0, 5).map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`assignee-${m.id}`}
            onClick={() => onChange(m.id)}
            className={[
              "rounded-full tap",
              sel?.id === m.id ? "ring-2 ring-foreground ring-offset-1" : "opacity-70",
            ].join(" ")}
            title={m.display_name}
          >
            <MemberAvatar member={m} size={28} ring={false} />
          </button>
        ))}
      </div>
    </div>
  );
}

function SwipeRow({ todo, members, expanded, onExpand, onToggle, onDelete, onPromote, onLongPress, onAddChecklist, onToggleChecklist, onLongPressSub }) {
  const [dx, setDx] = useState(0);
  const startX = useRef(0);
  const dragging = useRef(false);
  const [newSub, setNewSub] = useState("");
  const assignee = members.find((m) => m.id === todo.assignee_person_id);
  const hasChecklist = (todo.checklist || []).length > 0;
  const checklistDone = (todo.checklist || []).filter((c) => c.done).length;
  const isScheduled = !!todo.start_at;
  const longPress = useLongPress(() => onLongPress?.(todo));

  const onStart = (e) => {
    dragging.current = true;
    startX.current = (e.touches ? e.touches[0].clientX : e.clientX) - dx;
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - startX.current;
    setDx(Math.max(-120, Math.min(120, x)));
  };
  const onEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (dx > 80) { onToggle(); setDx(0); return; }
    if (dx < -80) { onDelete(); setDx(0); return; }
    setDx(0);
  };

  const bgLeft  = dx > 0 ? "bg-primary/90" : "bg-transparent";
  const bgRight = dx < 0 ? "bg-destructive/90" : "bg-transparent";

  const submitSub = (e) => {
    e.preventDefault();
    if (!newSub.trim()) return;
    onAddChecklist(newSub.trim());
    setNewSub("");
  };

  return (
    <div data-testid={`todo-${todo.id}`} className="relative select-none">
      <div className="relative overflow-hidden touch-pan-y">
        <div className={`absolute inset-0 flex items-center justify-start px-4 text-primary-foreground ${bgLeft}`}>
          {dx > 0 && <span className="inline-flex items-center gap-1 text-[12px] font-medium"><Check className="w-4 h-4" />{todo.done ? "Re-open" : "Done"}</span>}
        </div>
        <div className={`absolute inset-0 flex items-center justify-end px-4 text-primary-foreground ${bgRight}`}>
          {dx < 0 && <span className="inline-flex items-center gap-1 text-[12px] font-medium"><Trash2 className="w-4 h-4" />Delete</span>}
        </div>

        <div
          className="relative bg-card flex items-start gap-3 py-2.5 px-3"
          style={{ transform: `translateX(${dx}px)`, transition: dragging.current ? "none" : "transform 180ms ease-out" }}
          onTouchStart={(e) => { longPress.bind.onTouchStart(e); onStart(e); }}
          onTouchMove={(e) => { longPress.bind.onTouchMove(e); onMove(e); }}
          onTouchEnd={(e) => { longPress.bind.onTouchEnd(e); onEnd(e); }}
          onTouchCancel={longPress.bind.onTouchCancel}
          onMouseDown={(e) => { longPress.bind.onMouseDown(e); onStart(e); }}
          onMouseMove={(e) => { longPress.bind.onMouseMove(e); onMove(e); }}
          onMouseUp={(e) => { longPress.bind.onMouseUp(e); onEnd(e); }}
          onMouseLeave={(e) => { longPress.bind.onMouseLeave(e); onEnd(e); }}
          onContextMenu={longPress.bind.onContextMenu}
        >
          <button
            data-testid={`todo-toggle-${todo.id}`}
            onClick={onToggle}
            className={[
              "w-7 h-7 mt-0.5 rounded-md border-2 tap flex items-center justify-center shrink-0 transition-colors",
              todo.done ? "bg-primary border-primary text-primary-foreground" : "border-border bg-card",
            ].join(" ")}
            aria-label={todo.done ? "Mark as not done" : "Mark as done"}
          >
            {todo.done && <Check className="w-4 h-4" />}
          </button>
          <button
            onClick={onToggle}
            data-testid={`todo-row-${todo.id}`}
            className="flex-1 min-w-0 text-left tap active:bg-secondary/40 rounded-md -mx-1 px-1 transition-colors"
            aria-label={`Toggle ${todo.title}`}
          >
            <p className={["text-[15px] leading-snug flex items-center gap-1.5 flex-wrap", todo.done ? "line-through text-muted-foreground" : ""].join(" ")}>
              <span>{todo.title}</span>
              {hasChecklist && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onExpand(); }}
                  data-testid={`todo-checklist-badge-${todo.id}`}
                  className={[
                    "inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border tabular-nums tap cursor-pointer",
                    checklistDone === todo.checklist.length
                      ? "bg-primary/15 border-primary/30 text-primary"
                      : "bg-primary/10 border-primary/25 text-primary",
                  ].join(" ")}
                  title={expanded ? "Hide items" : "Show items"}
                >
                  {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {checklistDone}/{todo.checklist.length}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
              {assignee && (
                <span className="inline-flex items-center gap-1">
                  <MemberAvatar member={assignee} size={16} ring={false} />
                  {assignee.display_name?.split(" ")[0]}
                </span>
              )}
              {isScheduled && (
                <span data-testid={`todo-scheduled-badge-${todo.id}`} className="inline-flex items-center gap-1 text-primary">
                  <CalIcon className="w-3 h-3" />
                  {new Date(todo.start_at).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {todo.due_at && !isScheduled && (
                <span>· {fmtRelativeDate(todo.due_at)}</span>
              )}
              {todo.suggestion_source === "kinly" && (
                <span className="text-primary uppercase tracking-wider">· Kinly</span>
              )}
            </div>
          </button>
          {!todo.done && !isScheduled && (
            <button
              data-testid={`todo-promote-${todo.id}`}
              onClick={onPromote}
              className="text-[10px] uppercase tracking-wider text-primary font-medium tap px-1 mt-0.5"
              title="Schedule on calendar"
            >
              → cal
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="bg-secondary/30 px-3 py-2 pl-11 border-t border-border/40" data-testid={`todo-checklist-${todo.id}`}>
          {(todo.checklist || []).map((c) => (
            <SubItemRow
              key={c.id}
              sub={c}
              onToggle={() => onToggleChecklist(c.id, !c.done)}
              onLongPress={() => onLongPressSub?.(c)}
            />
          ))}
          <form onSubmit={submitSub} className="flex items-center gap-2 mt-1.5">
            <span className="w-4 h-4 rounded-sm border border-dashed border-border shrink-0" />
            <input
              data-testid={`subitem-input-${todo.id}`}
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              placeholder="Add sub-item…"
              className="flex-1 bg-transparent outline-none text-[13px] py-1 placeholder:text-muted-foreground/60"
            />
            {newSub.trim() && (
              <button type="submit" className="text-[11px] text-primary uppercase tracking-wider font-medium tap">Add</button>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

function ScheduleSheet({ open, todo, onClose, onConfirm }) {
  const tomorrow9 = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; };
  const [when, setWhen] = useState(tomorrow9());
  const [allDay, setAllDay] = useState(false);

  useEffect(() => { if (open) { setWhen(tomorrow9()); setAllDay(false); } }, [open]);

  if (!open || !todo) return null;

  const quickPick = (mutate) => {
    const d = new Date(); mutate(d); setWhen(d);
  };

  const onAccept = () => {
    onConfirm({ start_at: when.toISOString(), all_day: allDay });
  };

  const local = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <BottomSheet open={true} onClose={onClose} title={`Schedule "${todo.title}"`}>
      <div className="space-y-5" data-testid="schedule-sheet">
        <p className="text-[13px] text-muted-foreground">
          Pick a time. The to-do stays alive — when you tick it, it clears from both
          your list and your calendar.
        </p>

        <div className="flex flex-wrap gap-2">
          <QuickChip label="Today 6 PM"    onClick={() => quickPick((d) => { d.setHours(18, 0, 0, 0); })} />
          <QuickChip label="Tomorrow 9 AM" onClick={() => quickPick((d) => { d.setDate(d.getDate()+1); d.setHours(9, 0, 0, 0); })} />
          <QuickChip label="This weekend"  onClick={() => quickPick((d) => { const day = d.getDay(); const offset = (6 - day + 7) % 7 || 1; d.setDate(d.getDate()+offset); d.setHours(10, 0, 0, 0); })} />
          <QuickChip label="Next week"     onClick={() => quickPick((d) => { d.setDate(d.getDate()+7); d.setHours(9, 0, 0, 0); })} />
        </div>

        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">When</span>
          <input
            data-testid="schedule-when"
            type="datetime-local"
            value={local(when)}
            onChange={(e) => setWhen(new Date(e.target.value))}
            className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none mt-1"
          />
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="accent-primary" />
          All day
        </label>

        <button
          data-testid="schedule-confirm"
          onClick={onAccept}
          className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap"
        >
          Schedule
        </button>
      </div>
    </BottomSheet>
  );
}

function QuickChip({ label, onClick }) {
  return (
    <button onClick={onClick} className="h-8 px-3 rounded-full border border-border bg-card text-[12px] font-medium tap">
      {label}
    </button>
  );
}

function ListChip({ list, active, onTap, onLongPress }) {
  const longPress = useLongPress(() => onLongPress?.());
  // If the long-press fires, swallow the trailing click so we don't ALSO
  // activate the list under the menu (annoying flicker).
  const onClick = () => {
    if (longPress.didFire()) return;
    onTap?.();
  };
  return (
    <button
      data-testid={`todo-list-${list.id}`}
      onClick={onClick}
      onTouchStart={longPress.bind.onTouchStart}
      onTouchMove={longPress.bind.onTouchMove}
      onTouchEnd={longPress.bind.onTouchEnd}
      onTouchCancel={longPress.bind.onTouchCancel}
      onMouseDown={longPress.bind.onMouseDown}
      onMouseMove={longPress.bind.onMouseMove}
      onMouseUp={longPress.bind.onMouseUp}
      onMouseLeave={longPress.bind.onMouseLeave}
      onContextMenu={longPress.bind.onContextMenu}
      className={[
        "shrink-0 h-9 px-4 rounded-full tap text-sm font-medium border select-none",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-card",
      ].join(" ")}
    >
      {list.name}
    </button>
  );
}

function SubItemRow({ sub, onToggle, onLongPress }) {
  const longPress = useLongPress(() => onLongPress?.());
  const onClick = (e) => {
    if (longPress.didFire()) { e.preventDefault(); return; }
    onToggle();
  };
  return (
    <button
      type="button"
      data-testid={`subitem-${sub.id}`}
      onClick={onClick}
      onTouchStart={longPress.bind.onTouchStart}
      onTouchMove={longPress.bind.onTouchMove}
      onTouchEnd={longPress.bind.onTouchEnd}
      onTouchCancel={longPress.bind.onTouchCancel}
      onMouseDown={longPress.bind.onMouseDown}
      onMouseMove={longPress.bind.onMouseMove}
      onMouseUp={longPress.bind.onMouseUp}
      onMouseLeave={longPress.bind.onMouseLeave}
      onContextMenu={longPress.bind.onContextMenu}
      className="w-full flex items-center gap-2.5 py-2 tap text-left active:bg-secondary/60 rounded-md -mx-1 px-1 transition-colors select-none"
      aria-label={sub.done ? `Mark "${sub.title}" as not done` : `Mark "${sub.title}" as done`}
    >
      <span
        className={[
          "w-5 h-5 rounded-sm border tap flex items-center justify-center shrink-0 transition-colors",
          sub.done ? "bg-primary border-primary text-primary-foreground" : "border-border bg-card",
        ].join(" ")}
        aria-hidden="true"
      >
        {sub.done && <Check className="w-3 h-3" />}
      </span>
      <span className={["text-[14px] leading-tight flex-1 min-w-0", sub.done ? "line-through text-muted-foreground" : ""].join(" ")}>
        {sub.title}
      </span>
    </button>
  );
}

function SubItemEditSheet({ open, sub, busy, onClose, onSubmit, onDelete }) {
  const [v, setV] = useState("");
  useEffect(() => { if (open && sub) setV(sub.title || ""); }, [open, sub]);
  if (!open || !sub) return null;
  const submit = (e) => {
    e.preventDefault();
    const t = v.trim();
    if (!t) return;
    onSubmit(t);
  };
  return (
    <BottomSheet open onClose={onClose} title="Edit item">
      <form onSubmit={submit} className="space-y-4 pb-2" data-testid={`subitem-edit-${sub.id}`}>
        <input
          autoFocus
          data-testid="subitem-edit-input"
          value={v}
          onChange={(e) => setV(e.target.value)}
          className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[16px]"
        />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onDelete} disabled={busy}
            data-testid="subitem-edit-delete"
            className="h-11 rounded-2xl border border-destructive/40 text-destructive text-sm font-medium tap inline-flex items-center justify-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button type="submit" disabled={busy || !v.trim()}
            data-testid="subitem-edit-save"
            className="h-11 rounded-2xl bg-foreground text-background text-sm font-medium tap disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
            <Pencil className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}

function groupTodosBySection(items) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(now); endOfWeek.setDate(now.getDate() + (7 - ((now.getDay() + 6) % 7)));
  const today = [], thisWeek = [], later = [], done = [];
  for (const t of items) {
    if (t.done) { done.push(t); continue; }
    if (!t.due_at) { later.push(t); continue; }
    const due = new Date(t.due_at);
    if (due <= now || due.toDateString() === new Date().toDateString()) today.push(t);
    else if (due <= endOfWeek) thisWeek.push(t);
    else later.push(t);
  }
  if (today.length === 0 && thisWeek.length === 0 && later.length > 0 && items.filter(i=>!i.done).length === later.length) {
    return [
      { key: "all", label: "To do", items: later },
      { key: "done", label: "Done", items: done },
    ];
  }
  return [
    { key: "today", label: "Today", items: today },
    { key: "week", label: "This week", items: thisWeek },
    { key: "later", label: "Later", items: later },
    { key: "done", label: "Done", items: done },
  ];
}

function RecipeSheet({ open, list, familyId, onClose, onAdded }) {
  const [text, setText] = useState("");
  const [stage, setStage] = useState("input"); // input | extracting | preview | saving | error
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) { setText(""); setStage("input"); setItems([]); setPicked(new Set()); setError(""); }
  }, [open]);

  if (!open || !list) return null;

  const parsePreview = async () => {
    if (text.trim().length < 20) { setError("Paste a bit more of the recipe first."); return; }
    setStage("saving"); setError("");
    try {
      const { data } = await api.post(
        `/families/${familyId}/todo-lists/${list.id}/recipe-parse`,
        { text: text.trim(), create: false },
      );
      if (data?.error === "llm_unavailable" || !data?.items?.length) {
        setStage("input");
        setError(data?.error === "llm_unavailable"
          ? "Kinly's offline — try again in a moment."
          : "Couldn't pick out any ingredients. Try a clearer recipe?");
        return;
      }
      setItems(data.items);
      setPicked(new Set(data.items.map((_, i) => i)));
      setStage("preview");
    } catch (err) {
      setStage("input");
      setError(err.response?.data?.detail || "Couldn't reach Kinly — try again?");
    }
  };

  const parsePhoto = async (file) => {
    if (!file) return;
    if (file.size < 200) { setError("Photo too small — try again?"); return; }
    if (file.size > 10 * 1024 * 1024) { setError("Photo too large (max 10MB)."); return; }
    setStage("extracting"); setError("");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(
        `/families/${familyId}/todo-lists/${list.id}/recipe-from-photo?create=false`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (data?.error === "no_text_extracted") {
        setStage("input");
        setError("No recipe text found in the photo. Try a clearer shot of the ingredients list?");
        return;
      }
      if (data?.error === "llm_unavailable" || !data?.items?.length) {
        setStage("input");
        setError(data?.error === "llm_unavailable"
          ? "Kinly's offline — try again in a moment."
          : "Couldn't pick out any ingredients. Try a clearer photo?");
        return;
      }
      // Pre-fill the textarea with what Kinly saw, so the user can refine before
      // confirming. They can still edit and re-parse if the OCR misread.
      if (data?.vision?.text) setText(data.vision.text);
      setItems(data.items);
      setPicked(new Set(data.items.map((_, i) => i)));
      setStage("preview");
    } catch (err) {
      setStage("input");
      setError(err.response?.data?.detail || "Couldn't reach Kinly — try again?");
    }
  };

  const togglePick = (i) => {
    setPicked((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  const addPicked = async () => {
    setStage("saving");
    const chosen = items.filter((_, i) => picked.has(i));
    // POST each in sequence — simple, low-volume.
    try {
      for (const it of chosen) {
        const title = it.quantity ? `${it.title} (${it.quantity})` : it.title;
        await api.post(`/families/${familyId}/todo-lists/${list.id}/items`, { title });
      }
      onAdded?.();
    } catch (err) {
      setStage("preview");
      setError(err.response?.data?.detail || "Couldn't add the items — try again?");
    }
  };

  return (
    <BottomSheet open={true} onClose={onClose} title={stage === "preview" ? "Pick what to add" : `Recipe → ${list.name}`}>
      <div className="space-y-4" data-testid="recipe-sheet">
        <input
          ref={fileInputRef}
          data-testid="recipe-photo-input"
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => parsePhoto(e.target.files?.[0])}
        />
        {stage === "extracting" ? (
          <div className="py-12 text-center">
            <div className="inline-flex items-center gap-3 text-muted-foreground">
              <Camera className="w-5 h-5 animate-pulse" />
              <span className="italic">Kinly's reading the photo…</span>
            </div>
          </div>
        ) : stage === "input" || stage === "error" ? (
          <>
            <p className="text-[13px] text-muted-foreground">
              Snap a recipe — magazine page, handwritten card, even a screenshot —
              or paste the ingredients. Kinly fans them out as ticked items on{" "}
              <span className="text-foreground font-medium">{list.name}</span>.
            </p>
            <button
              type="button"
              data-testid="recipe-camera-btn"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-12 rounded-2xl border border-primary/30 bg-primary/[0.06] text-primary font-medium tap inline-flex items-center justify-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Snap a recipe
            </button>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              <span className="flex-1 h-px bg-border" />
              or paste
              <span className="flex-1 h-px bg-border" />
            </div>
            <textarea
              data-testid="recipe-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"e.g.\n500g beef mince\n2 onions, diced\n3 cloves garlic\n400g tomato passata\n…"}
              rows={7}
              className="w-full bg-card border border-border rounded-2xl p-3 outline-none text-[14px] focus:border-primary resize-none placeholder:text-muted-foreground/60"
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button
              data-testid="recipe-parse"
              onClick={parsePreview}
              disabled={text.trim().length < 20}
              className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap disabled:opacity-40"
            >
              Parse with Kinly
            </button>
          </>
        ) : stage === "preview" ? (
          <>
            <p className="text-[13px] text-muted-foreground">
              Tap to toggle. <span className="text-foreground font-medium">{picked.size}</span> of {items.length} selected.
            </p>
            <ul className="divide-y divide-border/60 bg-card border border-border rounded-2xl overflow-hidden max-h-[55vh] overflow-y-auto">
              {items.map((it, i) => {
                const on = picked.has(i);
                return (
                  <li key={i}>
                    <button
                      data-testid={`recipe-item-${i}`}
                      onClick={() => togglePick(i)}
                      className="w-full flex items-center gap-3 py-2.5 px-3 tap text-left"
                    >
                      <span className={[
                        "w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0",
                        on ? "bg-primary border-primary text-primary-foreground" : "border-border",
                      ].join(" ")}>
                        {on && <Check className="w-3 h-3" />}
                      </span>
                      <span className="flex-1 min-w-0 text-[14px]">{it.title}</span>
                      {it.quantity && (
                        <span className="text-[11px] text-muted-foreground font-mono shrink-0">{it.quantity}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => setStage("input")}
                className="h-12 px-4 rounded-2xl border border-border text-foreground font-medium tap"
              >
                Back
              </button>
              <button
                data-testid="recipe-confirm"
                onClick={addPicked}
                disabled={picked.size === 0}
                className="flex-1 h-12 rounded-2xl bg-foreground text-background font-medium tap disabled:opacity-40"
              >
                Add {picked.size} to {list.name}
              </button>
            </div>
          </>
        ) : (
          <div className="py-12 text-center text-muted-foreground italic">
            Kinly's reading the recipe…
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

